import React, { useState, useCallback, useRef, useEffect } from "react";
import AccordionItem from "./components/AccordionItem";
import AccordionShimmer from "./components/AccordionShimmer";

interface AccordionItem {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

interface AccordionProps {
  items: AccordionItem[];
  allowMultipleExpanded?: boolean;
  allowZeroExpanded?: boolean;
  preExpanded?: string[];
  accordionContainerClassName?: string;
  accordionPanelClassName?: string;
  accordionButtonClassName?: string;
  onChange?: (expandedIds: string[]) => void;
  isLoading?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultipleExpanded = false,
  allowZeroExpanded = false,
  preExpanded = [],
  accordionContainerClassName = "",
  accordionPanelClassName = "",
  accordionButtonClassName = "",
  onChange,
  isLoading = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    new Set(preExpanded)
  );
  const [lastToggledSection, setLastToggledSection] = useState<string | null>(
    null
  );

  const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const isScrollable =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (isScrollable && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }

      if (parent === document.body || parent === document.documentElement) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return document.documentElement;
  };

  const isElementInView = (
    element: HTMLElement,
    container: HTMLElement
  ): boolean => {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return (
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom
    );
  };

  const scrollToElement = useCallback((element: HTMLElement) => {
    if (!scrollContainerRef.current) return;

    const scrollableContainer = findScrollableParent(
      scrollContainerRef.current
    );
    if (!scrollableContainer) return;

    if (isElementInView(element, scrollableContainer)) {
      return;
    }

    const containerRect = scrollableContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const scrollTop =
      scrollableContainer.scrollTop +
      (elementRect.top - containerRect.top) -
      20;

    scrollableContainer.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (lastToggledSection && expandedItems.has(lastToggledSection)) {
      const sectionElement = sectionRefs.current[lastToggledSection];
      if (sectionElement) {
        setTimeout(() => {
          scrollToElement(sectionElement);
        }, 150);
      }
    }
    setLastToggledSection(null);
  }, [expandedItems, lastToggledSection, scrollToElement]);

  const handleToggle = useCallback(
    (id: string) => {
      const item = items?.find((item) => item.id === id);

      if (item?.disabled) return;

      setExpandedItems((prev) => {
        const newExpanded = new Set(prev);

        if (newExpanded.has(id)) {
          if (allowZeroExpanded || newExpanded.size > 1) {
            newExpanded.delete(id);
          }
        } else {
          if (!allowMultipleExpanded) {
            newExpanded.clear();
          }
          newExpanded.add(id);

          setLastToggledSection(id);
        }

        const expandedArray = Array.from(newExpanded);
        onChange?.(expandedArray);
        return newExpanded;
      });
    },
    [allowMultipleExpanded, allowZeroExpanded, onChange, items]
  );

  return (
    <div
      className={`w-full flex flex-col ${accordionContainerClassName}`}
      role="region"
      aria-label="Accordion"
      ref={scrollContainerRef}
    >
      {isLoading && <AccordionShimmer />}
      {!isLoading &&
        items.map((item) => (
          <AccordionItem
            key={item.id}
            item={item}
            isExpanded={expandedItems.has(item.id)}
            onToggle={handleToggle}
            accordionPanelClassName={accordionPanelClassName}
            accordionButtonClassName={accordionButtonClassName}
            ref={(el) => (sectionRefs.current[item.id] = el)}
          />
        ))}
    </div>
  );
};

export default Accordion;
